/**
 * Certificate element positioning system
 * Replaces the old monolithic textPositions (titleTop/nameTop/metadataTop)
 * with per-element x,y positioning for true drag-and-drop flexibility.
 */

export interface CertificateElementPosition {
  x: number; // % of certificate width (0-100)
  y: number; // % of certificate height (0-100)
}

export interface CertificateElementStyle {
  fontSize?: string; // Tailwind token: xs | sm | base | lg | xl | 2xl | 3xl | 4xl | 5xl
  fontWeight?: "normal" | "semibold" | "bold";
  letterSpacing?: string; // e.g., "0.2em"
  uppercase?: boolean;
  opacity?: number; // 0-1
  textAlign?: "left" | "center" | "right";
  color?: string; // Cor individual do elemento
}

export interface CertificateElement {
  id: CertificateElementId;
  enabled: boolean;
  position: CertificateElementPosition;
  style: CertificateElementStyle;
  width?: number; // absolute width in pixels; undefined = fit-content
}

export type CertificateElementId =
  | "title"
  | "titleDivider"
  | "introText"
  | "participantName"
  | "nameUnderline"
  | "cpf"
  | "participationText"
  | "eventName"
  | "metadataRow"
  | "conclusionText"
  | "signatures"
  | "validationLabel"
  | "validationUrl"
  | "certificateCode";

export const ALL_ELEMENT_IDS: CertificateElementId[] = [
  "title",
  "titleDivider",
  "introText",
  "participantName",
  "nameUnderline",
  "cpf",
  "participationText",
  "eventName",
  "metadataRow",
  "conclusionText",
  "signatures",
  "validationLabel",
  "validationUrl",
  "certificateCode",
];

/**
 * Chave i18n do nome de cada elemento, para o editor.
 *
 * Um `Record` explícito, e não `\`elementNames.${id}\`` montado na hora: a
 * camada de i18n do gate varre o código atrás de chamadas literais, e uma
 * chave montada em tempo de execução passa pela varredura — se faltar no
 * locale, aparece crua na tela do produtor.
 *
 * Isto é o NOME DO ELEMENTO NO EDITOR, não o texto impresso no certificado.
 * O que sai no papel é congelado no snapshot na emissão e não muda com o
 * idioma de quem está olhando: um certificado emitido em português continua em
 * português para sempre, inclusive para quem abrir a página em inglês.
 */
export const ELEMENT_LABEL_KEYS: Record<CertificateElementId, string> = {
  title: "elementNames.title",
  titleDivider: "elementNames.titleDivider",
  introText: "elementNames.introText",
  participantName: "elementNames.participantName",
  nameUnderline: "elementNames.nameUnderline",
  cpf: "elementNames.cpf",
  participationText: "elementNames.participationText",
  eventName: "elementNames.eventName",
  metadataRow: "elementNames.metadataRow",
  conclusionText: "elementNames.conclusionText",
  signatures: "elementNames.signatures",
  validationLabel: "elementNames.validationLabel",
  validationUrl: "elementNames.validationUrl",
  certificateCode: "elementNames.certificateCode",
};

/**
 * Rótulos em português, mantidos como reserva.
 *
 * Ainda usados por telas que não têm o `t` à mão. Saem quando a última migrar.
 */
export const ELEMENT_LABELS: Record<CertificateElementId, string> = {
  title: "Título",
  titleDivider: "Divisor do título",
  introText: "Texto de introdução",
  participantName: "Nome do participante",
  nameUnderline: "Sublinhado do nome",
  cpf: "CPF",
  participationText: "Texto de participação",
  eventName: "Nome do evento",
  metadataRow: "Dados do evento (data, local, etc.)",
  conclusionText: "Texto de conclusão",
  signatures: "Assinaturas",
  validationLabel: "Label de validação",
  validationUrl: "URL de validação",
  certificateCode: "Código do certificado",
};

// Default positions based on the old 3-block layout
export function getDefaultElements(): CertificateElement[] {
  return [
    {
      id: "title",
      enabled: true,
      position: { x: 50, y: 20 },
      style: { fontSize: "xl", fontWeight: "semibold", uppercase: true, letterSpacing: "0.05em", textAlign: "center" },
    },
    {
      id: "titleDivider",
      enabled: true,
      position: { x: 50, y: 26 },
      style: { textAlign: "center" },
    },
    {
      id: "introText",
      enabled: true,
      position: { x: 50, y: 38 },
      style: { fontSize: "xs", uppercase: true, letterSpacing: "0.2em", textAlign: "center", opacity: 0.6 },
    },
    {
      id: "participantName",
      enabled: true,
      position: { x: 50, y: 45 },
      style: { fontSize: "4xl", fontWeight: "bold", textAlign: "center" },
    },
    {
      id: "nameUnderline",
      enabled: true,
      position: { x: 50, y: 52 },
      style: { textAlign: "center" },
    },
    {
      id: "cpf",
      enabled: false,
      position: { x: 50, y: 55 },
      style: { fontSize: "xs", uppercase: true, letterSpacing: "0.18em", textAlign: "center", opacity: 0.55 },
    },
    {
      id: "participationText",
      enabled: true,
      position: { x: 50, y: 62 },
      style: { fontSize: "sm", textAlign: "center", opacity: 0.65 },
    },
    {
      id: "eventName",
      enabled: true,
      position: { x: 50, y: 67 },
      style: { fontSize: "2xl", fontWeight: "semibold", textAlign: "center" },
    },
    {
      id: "metadataRow",
      enabled: true,
      position: { x: 50, y: 74 },
      style: { textAlign: "center" },
    },
    {
      id: "conclusionText",
      enabled: true,
      position: { x: 50, y: 82 },
      style: { fontSize: "xs", textAlign: "center", opacity: 0.5 },
    },
    {
      id: "signatures",
      enabled: true,
      position: { x: 15, y: 90 },
      style: { textAlign: "left" },
    },
    {
      id: "validationLabel",
      enabled: true,
      position: { x: 85, y: 88 },
      style: { fontSize: "xs", uppercase: true, letterSpacing: "0.2em", textAlign: "right", opacity: 0.7 },
    },
    {
      id: "validationUrl",
      enabled: true,
      position: { x: 85, y: 91 },
      style: { fontSize: "xs", textAlign: "right", opacity: 0.55 },
    },
    {
      id: "certificateCode",
      enabled: true,
      position: { x: 85, y: 94 },
      style: { fontSize: "xs", textAlign: "right", opacity: 0.35 },
    },
  ];
}

// Convert old config format to new element-based format
export function migrateLegacyPositions(
  textPositions: { titleTop: number; nameTop: number; metadataTop: number },
  textConfig: { title: string; introText: string; participationText: string; conclusionText: string },
  fontSizes: { title: string; name: string; event: string },
  fields: { showCPF?: boolean; showSigners?: boolean; showQRCode?: boolean }
): CertificateElement[] {
  const elements = getDefaultElements();

  // Map old bottom-% positions to new top-% positions
  // Old: 80 (near top) -> New: y=20
  // Old: 55 (middle) -> New: y=45
  // Old: 35 (lower) -> New: y=65
  const titleY = Math.max(5, Math.min(95, 100 - textPositions.titleTop + 5));
  const nameY = Math.max(5, Math.min(95, 100 - textPositions.nameTop + 5));
  const metadataY = Math.max(5, Math.min(95, 100 - textPositions.metadataTop + 5));

  elements.forEach((el) => {
    switch (el.id) {
      case "title":
        el.position.y = titleY - 5;
        el.enabled = !!textConfig.title;
        if (fontSizes.title) el.style.fontSize = fontSizes.title;
        break;
      case "titleDivider":
        el.position.y = titleY;
        el.enabled = !!textConfig.title;
        break;
      case "introText":
        el.position.y = nameY - 10;
        el.enabled = !!textConfig.introText;
        break;
      case "participantName":
        el.position.y = nameY - 2;
        break;
      case "nameUnderline":
        el.position.y = nameY + 4;
        break;
      case "cpf":
        el.position.y = nameY + 8;
        el.enabled = !!fields.showCPF;
        break;
      case "participationText":
        el.position.y = metadataY - 5;
        el.enabled = !!textConfig.participationText;
        break;
      case "eventName":
        el.position.y = metadataY;
        break;
      case "metadataRow":
        el.position.y = metadataY + 7;
        break;
      case "conclusionText":
        el.position.y = metadataY + 15;
        el.enabled = !!textConfig.conclusionText;
        break;
      case "signatures":
        el.enabled = fields.showSigners !== false;
        break;
      case "validationLabel":
      case "validationUrl":
      case "certificateCode":
        el.enabled = fields.showQRCode !== false;
        break;
    }
  });

  return elements;
}
